import { call, put, takeLatest } from 'redux-saga/effects';
import { CloudActions } from '@portal/portal-core';

import { LicenseError } from '../shared';
import { sendAction } from '../api';
import { showLicenseManageSuccess, showLicenseManageError } from '../actions';
import { SUPER_ADMIN_BULK_ACTIONS, SUPER_ADMIN_LICENSE_ERR } from '../types';

// Minimal shape of the superadmin API config passed through to `sendAction`.
interface SuperClientConfig {
  baseApiUrl: string;
  getAuthToken: () => string;
  timeout?: number;
}

// Action dispatched to trigger the bulk-actions saga.
interface BulkActionsAction {
  type: string;
  actions?: unknown[];
}

// Single result from an ADMIN_BATCH_ACTIONS response.
interface BatchActionResult {
  succeeded: boolean;
  type: string;
}

// Wrapped response returned by `sendAction`.
interface BatchActionsResponse {
  failed?: boolean;
  data?: { results?: BatchActionResult[] };
}

// Shape of the `LicenseError` we know how to handle (imported from untyped `.js`).
interface HandledLicenseError {
  type?: string;
  actions?: unknown[];
}

// Handle multiple independent superadmin actions by batching them into single request
export function* bulkActions(
  config: SuperClientConfig,
  action: BulkActionsAction,
): Generator<unknown, void, unknown> {
  try {
    const { actions = [] } = action;

    if (actions.length === 0) {
      yield put(showLicenseManageSuccess());
      return;
    }

    // Batch all actions into a single ADMIN_BATCH_ACTIONS request
    const batchAction = CloudActions.adminBatchActions(actions);
    const response = (yield call(
      sendAction,
      config,
      batchAction,
    )) as BatchActionsResponse;

    if (response.failed) {
      throw new LicenseError([]);
    }

    const { results = [] } = response.data || {};
    const failedResults = results.filter((r) => !r.succeeded);
    if (failedResults.length > 0) {
      throw new LicenseError(failedResults.map((r) => r.type));
    }

    yield put(showLicenseManageSuccess());
    yield put(CloudActions.getCostCentersVerbose());
  } catch (err) {
    const licenseErr = err as HandledLicenseError;
    // Show modal for errors we know how to deal with:
    if (licenseErr.type === SUPER_ADMIN_LICENSE_ERR) {
      yield put(showLicenseManageError(licenseErr.actions as unknown[]));
    } else {
      console.error(err);
    }
    yield put(CloudActions.getCostCentersVerbose());
  }
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchBulkActions(
  config: SuperClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(SUPER_ADMIN_BULK_ACTIONS, bulkActions, config);
}
